import { motion, useReducedMotion } from 'framer-motion';
import type { ReactNode } from 'react';

/**
 * RevealText — splits children into words and reveals them in sequence.
 * Used for editorial-style openers. Reduces motion when the user prefers it.
 */
export function RevealText({
  children,
  className = '',
  delay = 0,
  stagger = 0.06,
  as: Tag = 'span',
}: {
  children: string;
  className?: string;
  delay?: number;
  stagger?: number;
  as?: 'span' | 'h1' | 'h2' | 'h3' | 'p' | 'div';
}) {
  const reduce = useReducedMotion();
  const words = children.split(' ');

  if (reduce) {
    return <Tag className={className}>{children}</Tag>;
  }

  return (
    <Tag className={className}>
      {words.map((word, i) => (
        <motion.span
          key={i}
          initial={{ y: '110%', opacity: 0 }}
          animate={{ y: '0%', opacity: 1 }}
          transition={{
            duration: 0.7,
            ease: [0.16, 1, 0.3, 1],
            delay: delay + i * stagger,
          }}
          style={{ display: 'inline-block', whiteSpace: 'pre' }}
        >
          {word}
          {i < words.length - 1 ? ' ' : ''}
        </motion.span>
      ))}
    </Tag>
  );
}

/** A single word wrapped in a marker-style underline */
export function InkUnderline({ children, soft = false }: { children: ReactNode; soft?: boolean }) {
  return (
    <span className={`ink-underline ${soft ? 'ink-underline-soft' : ''}`}>{children}</span>
  );
}
